
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function HomePage() {
  const steps = [
    {
      number: '01',
      title: 'Apply Online',
      description: 'Complete our simple application in just minutes.',
      icon: 'ri-file-text-line'
    },
    {
      number: '02',
      title: 'Intake & Review',
      description: 'Complete intake process and review house guidelines.',
      icon: 'ri-user-search-line'
    },
    {
      number: '03',
      title: 'Move In',
      description: 'Start rebuilding your life with structure and support.',
      icon: 'ri-home-heart-line'
    }
  ];

  const features = [
    {
      icon: 'ri-home-4-line',
      title: 'Furnished, Move-in Ready Rooms',
      description: 'Complete bedroom setups with bed, dresser, and personal storage space'
    },
    {
      icon: 'ri-wifi-line',
      title: 'Utilities and Wi-Fi Included',
      description: 'All essential services covered - electricity, water, heating, and high-speed internet'
    },
    {
      icon: 'ri-team-line',
      title: 'Supportive Community with Peer Accountability',
      description: 'Connect with fellow veterans in a respectful environment built on mutual support'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Access to Job, Benefits, and Financial Literacy Resources',
      description: 'Comprehensive support for employment, VA benefits navigation, and financial planning'
    }
  ];

  const impactStats = [
    {
      percentage: '87%',
      description: 'of residents transition to permanent housing within 12 months'
    },
    {
      percentage: '90%',
      description: 'report improved financial stability'
    },
    {
      percentage: '100%',
      description: 'receive access to benefits or job placement assistance'
    }
  ];

  const testimonials = [
    {
      quote: "This home gave me structure and peace of mind after service. I'm back on my feet and working again.",
      author: "Resident, Houston TX"
    },
    {
      quote: "RILG didn't just give me a room - they gave me a foundation to rebuild my life with dignity.",
      author: "Former Resident, Texas"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://public.readdy.ai/ai/img_res/3394205f7e5f00d135da1b607f844c9a.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Rise Independent Living for Veterans
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Housing, structure, and community for lasting independence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/apply">
                <Button size="lg" className="w-full sm:w-auto">
                  Apply for Housing
                </Button>
              </Link>
              <Link to="/referrals">
                <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white text-white hover:bg-white hover:text-gray-900">
                  Refer a Veteran
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            A mission-driven initiative providing housing, structure, and purpose for veterans.
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-12">
            RILG restores stability, dignity, and opportunity for U.S. veterans through structured residential housing. We create safe homes with purpose — not shelters — where veterans rebuild independence, discipline, and confidence.
          </p>
        </div>
      </section>

      {/* Rebuilding Lives Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Rebuilding Lives with Dignity
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            RILG provides affordable, structured independent living for veterans ready to rebuild their lives. 
            Our homes offer stability, safety, and access to supportive resources without the barriers of 
            institutional programs. Veterans maintain autonomy while receiving the structure needed for growth.
          </p>
        </div>
      </section>

      {/* Mission Snapshot */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                To restore stability, dignity, and independence for U.S. veterans through structured housing and community partnerships.
              </p>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Our Promise
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Every home we open represents safety, accountability, and a pathway to growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Simple steps to secure your independent living space
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide - Expanded */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Provide
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive support for independent living and long-term success
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4 p-6 rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`${feature.icon} text-blue-600 text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact & Outcomes */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
            Our Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-4">
                  {stat.percentage}
                </div>
                <p className="text-lg text-gray-300">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              From Our Veterans
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg">
                <div className="text-4xl text-blue-600 mb-4">"</div>
                <p className="text-lg text-gray-700 mb-6 italic">
                  {testimonial.quote}
                </p>
                <div className="text-gray-600 font-medium">
                  — {testimonial.author}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner With Us CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Partner With RILG
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              We provide structured homes for veterans. You provide the opportunity.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-settings-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Case managers get fast placement</h3>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Landlords get guaranteed rent</h3>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Communities gain stability</h3>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/partners">
              <Button variant="outline" size="lg" className="bg-white/10 border-white text-white hover:bg-white hover:text-blue-600">
                Learn About Partnership
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            For Partners
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            We collaborate with veteran organizations, property owners, and community programs 
            to expand access to stable housing. Together, we create lasting change.
          </p>
          <Link to="/partners">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Learn About Partnership
            </Button>
          </Link>
        </div>
      </section>

      {/* Quote Strip */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl md:text-3xl font-bold text-white italic">
            "Structure creates freedom. Housing builds opportunity."
          </blockquote>
          <p className="text-blue-200 mt-4 text-lg">— RILG Leadership</p>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 text-sm leading-relaxed">
            RILG – Rise Independent Living Group provides affordable veteran housing and structured independent living programs in Houston, Texas, and across the U.S. 
            Our mission-driven approach helps veterans transition from homelessness to independence through safe, supportive residential communities with comprehensive wraparound services.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Join Our Mission
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Help veterans rise with dignity and independence. Veterans gain more than a roof — they gain direction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/apply">
              <Button size="lg" className="w-full sm:w-auto">
                Apply Today
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-gray-900">
                Get In Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}